/**
 * The Inbox (inbox.md): a calm triage desk. One list of everything that needs you, grouped by
 * kind in urgency order, a scope strip on top (Needs you, Expiring soon, Done), and the selected
 * item in full in a side panel with one primary action. Docked from 1280 px, a drawer below.
 * Keyboard: j and k move; on the request that is open on screen a allows once, s for the session,
 * r makes a rule, d denies (decision.ts). Keys never act inside another dialog or menu, and never
 * on an item that just took the panel.
 */
import {
  BellOff,
  Check,
  ChevronDown,
  CircleCheckBig,
  CircleHelp,
  ClipboardList,
  FilePen,
  Globe,
  Hand,
  Inbox as InboxIcon,
  MessageCircleQuestion,
  MessagesSquare,
  Plug,
  PlugZap,
  Server,
  SquareTerminal,
  TextCursorInput,
  Timer,
  TimerOff,
  X,
} from '@orgus/ui/icons';
import type { LucideIcon } from '@orgus/ui/icons';
import { motion } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import type { AgentKind, ApprovalRequest, Session } from '@orgus/protocol';
import { sessionReason } from '@orgus/protocol';
import {
  Badge,
  Button,
  Checkbox,
  Drawer,
  EmptyState,
  GROUP_INDENT,
  IconTile,
  LiveNumber,
  Pressable,
  IconButton,
  ProjectMark,
  Segmented,
  Skeleton,
  Tooltip,
  WorkStateIcon,
  cn,
  formatDuration,
  formatInt,
  formatRelative,
  isTypingTarget,
  motionPresets,
  toUiError,
  toast,
  useNow,
  useSelection,
} from '@orgus/ui';
import type { IconTileTone } from '@orgus/ui';
import { TOKENS } from '@orgus/tokens';
import { useOrgus } from '../../lib/store.ts';
import { plainText } from '../../lib/plainText.ts';
import { useMediaQuery } from '../../lib/useMediaQuery.ts';
import { missionsSlice } from '../missions/model.ts';
import { personSteps } from './personSteps.ts';
import { useSessionTitle } from '../sessions/title.tsx';
import { useAssistantPending } from '../assistant/data.ts';
import { AgentGlyph } from '../adapters/AgentKind.tsx';
import { useWaitingOnYou } from './waiting.ts';
import { askSummary, decidedText, isAnswerable } from './askModel.ts';
import type { AskKind } from './askModel.ts';
import { useApprovals, useMockBurst } from './live.ts';
import { useBlockedApprovals } from './blocked.ts';
import { InboxDetail } from './InboxDetail.tsx';
import type { DetailContext, DetailParts } from './InboxDetail.tsx';
import { GROUP_LABEL, SECTION_ROWS, blockedCause, inboxSections, isSelectable, dismissedItems, doneItems, doneLabel, doneToday, expiringItems, inboxGroups, keyForApproval, nextAfter, openAsked, shownRows, shortTarget, titleParts, toolFamily } from './inboxModel.ts';
import { asksStateOf, askIdOf } from '@orgus/protocol';
import { dismissAsks } from '../asks/api.ts';
import { useWho } from '../asks/AskActions.tsx';
import { setAsideText } from '../asks/AskActions.tsx';
import type { InboxGrouping, InboxItem, InboxScope, ToolFamily } from './inboxModel.ts';
import { InboxBulk } from './InboxBulk.tsx';
import { INBOX_DRAWER, INBOX_SHORTCUTS, KEY_SETTLE_MS, otherOverlayOpen } from './keys.ts';
import { usePageShortcutHelp } from '../../lib/pageShortcuts.ts';
import type { BulkOutcome } from './InboxBulk.tsx';
import { RiskLabel } from './RiskLabel.tsx';

/** From this width the panel docks beside the list (breakpoint.xl). */
const DOCK_QUERY = `(min-width: ${String(TOKENS['breakpoint.xl'].value)})`;

const SURFACE = 'rounded-card border border-border bg-surface surface-sheen shadow-elevation-flat';

/** "4m", "42s", "2h": the largest unit only. */
const short = (ms: number) => formatDuration(Math.max(1000, ms)).split(' ')[0]!;

export function InboxSkeleton() {
  return (
    <div className="flex h-full min-h-0" role="status" aria-label="Loading the inbox">
      <div className="flex min-w-0 flex-1 flex-col gap-6 px-6 pt-6">
        <Skeleton className="h-20 rounded-card" />
        <div className="flex flex-col gap-2">
          <Skeleton className="h-4 w-32" />
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-row-2line rounded-card" />
          ))}
        </div>
      </div>
      <div className="m-2 ml-0 hidden w-(--drawer-width) shrink-0 xl:block">
        <Skeleton className="h-full rounded-overlay" />
      </div>
    </div>
  );
}

const TOOL_GLYPH: Record<ToolFamily, LucideIcon> = { shell: SquareTerminal, edit: FilePen, web: Globe, mcp: Plug, other: CircleHelp };
const ASK_GLYPH: Record<AskKind, LucideIcon> = { question: MessageCircleQuestion, plan: ClipboardList, elicitation: TextCursorInput };
const ASK_WORD: Record<AskKind, string> = { question: 'question', plan: 'plan to approve', elicitation: 'form' };

// ---------------------------------------------------------------------------
// The scope strip
// ---------------------------------------------------------------------------

interface Segment {
  id: InboxScope;
  label: string;
  /** The label on a phone, where three cells share 390 px. */
  short?: string;
  icon: ReactNode;
  tone: IconTileTone;
  value: number;
  fact: string;
  loud?: boolean;
}

/**
 * Three doors in the page's stat strip look (StatStrip and StatTile): one slim panel, cells set
 * apart by thin inset lines, a tinted icon tile beside each small label, the number under it and
 * one dim line. The chosen scope carries the one selected look, inset in its cell.
 */
function ScopeStrip({ segments, scope, onScope }: { segments: Segment[]; scope: InboxScope; onScope: (s: InboxScope) => void }) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  return (
    <div role="radiogroup" aria-label="Inbox scope" className="inset-dividers-row grid grid-cols-3 overflow-hidden rounded-card border border-border bg-surface shadow-elevation-flat">
      {segments.map((s, i) => {
        const on = s.id === scope;
        return (
          <Pressable
            key={s.id}
            ref={(el) => {
              refs.current[i] = el;
            }}
            role="radio"
            aria-checked={on}
            tabIndex={on ? 0 : -1}
            data-metric={s.id}
            onClick={() => onScope(s.id)}
            onKeyDown={(e) => {
              const d = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
              if (!d) return;
              e.preventDefault();
              const n = (i + d + segments.length) % segments.length;
              onScope(segments[n]!.id);
              refs.current[n]?.focus();
            }}
            className="group/seg relative flex min-w-0 flex-col gap-1 px-4 py-3 sm:px-6"
          >
            {on ? (
              <motion.span layoutId="inbox-scope" transition={motionPresets.move} className="selected-tint absolute inset-1 rounded-control" aria-hidden />
            ) : (
              <span className="absolute inset-1 rounded-control opacity-0 transition-opacity duration-(--duration-fast) group-hover/seg:bg-hover group-hover/seg:opacity-100" aria-hidden />
            )}
            <span className="relative flex min-w-0 items-center gap-2 text-caption text-fg-subtle">
              <IconTile icon={s.icon} tone={s.tone} size="sm" className="max-sm:hidden" />
              <span className={cn('truncate', on && 'text-fg-muted')}>
                <span className="sm:hidden">{s.short ?? s.label}</span>
                <span className="max-sm:hidden">{s.label}</span>
              </span>
            </span>
            <span className="relative flex min-w-0 items-baseline gap-2">
              <LiveNumber value={s.value} className={cn('font-display text-metric num-display', s.loud ? 'text-status-attention' : s.value ? 'text-fg' : 'text-fg-subtle')} />
              <span className="hidden min-w-0 truncate text-caption text-fg-subtle md:inline">{s.fact}</span>
            </span>
          </Pressable>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

interface RowView {
  lead: ReactNode;
  title: ReactNode;
  /** Up to two quiet facts under the title (the session with its agent, the project with its mark). */
  meta: ReactNode[];
  /** Under 640 px only the last meta fact stays (the project): the session gives way to the title. */
  narrowLast?: boolean;
  chip?: ReactNode;
  /** Keep the chip on a phone, under the time (the risk does; an ask's header chip does not). */
  chipNarrow?: boolean;
  trailing: ReactNode;
  label: string;
}

/** A row's box for bulk answers: shown on hover or focus, and on every row while anything is picked. */
interface RowCheck {
  checked: boolean;
  any: boolean;
  onCheck: (key: string, range: boolean) => void;
}

/**
 * One item, two lines: what it is (a verb and its target in small mono, or a question) over where
 * it comes from (the tool and the session, the project). At the right one chip at most (the risk,
 * at every level), then the time column, the same width on every row so the times and their bars
 * line up. All of it at every width. A box over the glyph picks the row for a bulk answer.
 */
function Row({ item, view, selected, onSelect, registerRef, check }: { item: InboxItem; view: RowView; selected: boolean; onSelect: (key: string) => void; registerRef: (key: string, el: HTMLElement | null) => void; check?: RowCheck }) {
  const meta = view.meta.filter((m) => m != null && m !== '' && m !== false);
  const shown = check && (check.any || check.checked);
  return (
    <li className="group/row motion-arrive relative" data-row ref={(el) => registerRef(item.key, el)}>
      {selected && (
        <motion.span layoutId="inbox-row" transition={motionPresets.move} className="selected-tint absolute inset-0 rounded-card" aria-hidden />
      )}
      <Pressable
        onClick={(e) => (check && e.shiftKey ? check.onCheck(item.key, true) : onSelect(item.key))}
        aria-current={selected || undefined}
        // One line tall when there is nothing under the title (a session's group says where it comes from).
        className={cn('relative flex items-center gap-3 rounded-card px-3', meta.length ? 'h-row-2line' : 'h-row', !selected && 'hover:bg-hover', check?.checked && !selected && 'bg-hover')}
      >
        <span className={cn('flex size-4 shrink-0 items-center justify-center text-fg-subtle transition-opacity duration-(--duration-fast) [&_svg]:size-4', check && (shown ? 'opacity-0' : 'group-hover/row:opacity-0 group-focus-within/row:opacity-0'))} aria-hidden>
          {view.lead}
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="flex min-w-0 items-baseline text-ui font-medium text-fg">
            {view.title}
            <span className="sr-only">, {view.label}</span>
          </span>
          {meta.length > 0 && (
            <span className={cn('flex min-w-0 items-center gap-3 text-caption', selected ? 'text-fg-muted' : 'text-fg-subtle')}>
              {meta.slice(0, 2).map((m, i, all) => (
                <span key={i} className={cn('flex min-w-0 items-center gap-1', i === 0 ? 'shrink' : 'max-w-1/2 shrink-0 max-sm:max-w-full', view.narrowLast && all.length > 1 && i === 0 && 'max-sm:hidden')} data-meta>
                  {m}
                </span>
              ))}
            </span>
          )}
        </span>
        {/* The chip has its own column from 640 px; on a phone it goes under the time, where the bar would be. */}
        {view.chip && <span className="hidden shrink-0 sm:inline-flex">{view.chip}</span>}
        {/* One line each, never wrapped: the times line up down the list (short forms on a phone). */}
        <span className={cn('flex w-16 shrink-0 flex-col items-end gap-1 whitespace-nowrap text-caption num sm:w-20', selected ? 'text-fg-muted' : 'text-fg-subtle')}>
          {view.trailing}
          {view.chip && view.chipNarrow !== false && <span className="flex max-w-full sm:hidden">{view.chip}</span>}
        </span>
      </Pressable>
      {check && (
        // After the row button, so it sits on top of it, over the glyph.
        <span className={cn('absolute inset-y-0 left-3 flex items-center transition-opacity duration-(--duration-fast)', !shown && 'opacity-0 group-hover/row:opacity-100 group-focus-within/row:opacity-100')}>
          <Checkbox aria-label={`Select ${view.label}`} checked={check.checked} tabIndex={-1} onCheckedChange={(_, e) => check.onCheck(item.key, (e.nativeEvent as MouseEvent).shiftKey)} />
        </span>
      )}
    </li>
  );
}

/** A trailing time in full from 640 px ("asked 7m ago"), and its short form on a phone ("7m"). */
function Span({ full, short: brief }: { full: string; short: string }) {
  return (
    <>
      <span className="max-sm:hidden">{full}</span>
      <span className="sm:hidden">{brief}</span>
    </>
  );
}

/** A row's title for a request: the verb in words, the target small in mono, cut from the middle, whole in the tooltip. */
function RequestTitle({ a }: { a: Pick<ApprovalRequest, 'title' | 'input'> }) {
  const p = titleParts(a);
  if (!p.target) return <span className="truncate">{p.verb}</span>;
  return (
    <span className="flex min-w-0 items-baseline gap-2">
      <span className="shrink-0">{p.verb}</span>
      <span className="min-w-0 truncate font-mono text-caption font-normal text-fg-muted" title={p.full}>
        {shortTarget(p, 52)}
      </span>
    </span>
  );
}

/** The session a request comes from, after its agent's mark. */
function SessionFact({ kind, name }: { kind: AgentKind | null | undefined; name: string }) {
  return (
    <>
      <AgentGlyph kind={kind} className="size-3 shrink-0" />
      <span className="truncate">{name}</span>
    </>
  );
}

/** The project after its mark (the initial in the project's hue); a machine when there is no project, or an MCP server. */
function PlaceFact({ projectId, name, server }: { projectId: string | null | undefined; name: string; server?: boolean }) {
  return (
    <>
      {projectId ? <ProjectMark projectId={projectId} name={name} size="xs" /> : server ? <Plug className="size-3 shrink-0" aria-hidden /> : <Server className="size-3 shrink-0" aria-hidden />}
      <span className="truncate">{name}</span>
    </>
  );
}

/** A group's select-all box: every row of the group, or clear them; mixed while some are picked. */
interface GroupCheck {
  checked: boolean;
  partial: boolean;
  any: boolean;
  onToggle: () => void;
}

/** A group's header, the shared look: its icon, label, count and one quiet fact. The rows under it sit one group step in (GROUP_INDENT). */
function GroupHeader({ label, count, meta, state = 'needs-you', lead, action, check }: { label: ReactNode; count: number; meta?: string | null; state?: 'needs-you' | 'asked' | 'landed'; lead?: ReactNode; action?: ReactNode; check?: GroupCheck }) {
  const shown = check && (check.any || check.checked || check.partial);
  const icon = lead ?? (state === 'needs-you' ? <WorkStateIcon state="needs-you" /> : state === 'asked' ? <MessageCircleQuestion className="size-4 text-fg-subtle" aria-hidden /> : <CircleCheckBig className="size-4 text-fg-subtle" aria-hidden />);
  return (
    <div className="group/head relative flex min-h-row items-center gap-2 px-3 py-1">
      <span className={cn('flex size-4 shrink-0 items-center justify-center transition-opacity duration-(--duration-fast)', check && (shown ? 'opacity-0' : 'group-hover/head:opacity-0 group-focus-within/head:opacity-0'))}>{icon}</span>
      {check && (
        <span className={cn('absolute inset-y-0 left-3 flex items-center transition-opacity duration-(--duration-fast)', !shown && 'opacity-0 group-hover/head:opacity-100 group-focus-within/head:opacity-100')}>
          <Checkbox aria-label={`Select every item in ${typeof label === 'string' ? label : 'this group'}`} checked={check.checked} indeterminate={check.partial} onCheckedChange={() => check.onToggle()} />
        </span>
      )}
      <h3 className="flex min-w-0 items-center gap-2 text-ui font-medium text-fg">{label}</h3>
      <LiveNumber value={count} className="shrink-0 text-ui text-fg-subtle" />
      {/* The meta is short (a place and a count), so it stays whole and the label gives way first. */}
      {meta && <span className="hidden shrink-0 whitespace-nowrap text-caption text-fg-subtle num sm:inline">{meta}</span>}
      {action && <span className="ml-auto flex shrink-0">{action}</span>}
    </div>
  );
}

/** What a bulk answer left undone, over the list: the count that went through, then each refusal and why. */
function OutcomeBanner({ outcome, onClose }: { outcome: BulkOutcome; onClose: () => void }) {
  return (
    <div role="alert" className="motion-arrive flex items-start gap-3 rounded-card bg-status-danger-soft px-4 py-3 text-ui text-fg">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p>{outcome.line}</p>
        {outcome.failures.length > 0 && (
          <ul className="flex flex-col gap-1 text-caption text-fg-muted">
            {outcome.failures.slice(0, 5).map((f) => (
              <li key={f.id} className="min-w-0">
                <span className="font-mono text-fg">{f.title}</span> {f.why}
              </li>
            ))}
            {outcome.failures.length > 5 && <li>+{outcome.failures.length - 5}</li>}
          </ul>
        )}
      </div>
      <IconButton size="xs" label="Close" icon={<X />} onClick={onClose} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

export function InboxView() {
  const { phase, approvals } = useApprovals();
  useMockBurst();
  usePageShortcutHelp('Inbox', INBOX_SHORTCUTS);
  const blocked = useBlockedApprovals().items;
  const steps = useAssistantPending();
  const waiting = useWaitingOnYou();
  const now = useNow(15_000);
  const wide = useMediaQuery(DOCK_QUERY);
  const { machines, projects, sessions } = useOrgus(useShallow((s) => ({ machines: s.machines, projects: s.projects, sessions: s.sessions })));
  const missions = useOrgus((s) => missionsSlice(s.ext)?.missions);
  const titleOf = useSessionTitle();
  // QA-64: steps a mission's agent left for a person wait here too, until the mission is closed.
  const leftForPerson = useMemo(() => personSteps(missions ?? []), [missions]);
  const go = useNavigate();
  const [params, setParams] = useSearchParams();
  const scope: InboxScope = params.get('view') === 'done' ? 'done' : params.get('view') === 'expiring' ? 'expiring' : 'open';
  const grouping: InboxGrouping = params.get('group') === 'session' ? 'session' : params.get('group') === 'tool' ? 'tool' : 'kind';
  const [selected, setSelected] = useState<string | null>(null);
  /** Sections opened past SECTION_ROWS, by key. */
  const [whole, setWhole] = useState<ReadonlySet<string>>(() => new Set());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const rowRefs = useRef(new Map<string, HTMLElement>());
  /** The open request's answer keys (RequestDetail binds them while it is on screen). */
  const answerKeys = useRef<((e: KeyboardEvent) => boolean) | null>(null);
  const bindKeys = useCallback((fn: ((e: KeyboardEvent) => boolean) | null) => {
    answerKeys.current = fn;
  }, []);
  /** When the open item last changed: an answer key this soon after is ignored, so it never lands on an item that just arrived. */
  const shownAt = useRef(0);

  // The one selector (ADR 0056): blocked sessions, then asked questions below everything that blocks.
  const groups = useMemo(() => inboxGroups({ pending: approvals?.pending ?? [], blocked, steps, questions: waiting.sessions, asked: waiting.asked, open: waiting.needs.open.map((i) => i.session!) }), [approvals?.pending, blocked, steps, waiting.sessions, waiting.asked, waiting.needs.open]);
  const expiring = useMemo(() => expiringItems(groups, now), [groups, now]);
  // Done: decided requests and, for 7 days, dismissed and snoozed questions with Undo (ADR 0056).
  const asksExt = useOrgus((s) => s.ext.asks);
  const setAsideList = useMemo(() => asksStateOf({ asks: asksExt }).setAside, [asksExt]);
  const done = useMemo(() => [...doneItems(approvals?.recent ?? []), ...dismissedItems(setAsideList, sessions)].sort((x, y) => y.at - x.at), [approvals?.recent, setAsideList, sessions]);
  const who = useWho();
  const [bulk, setBulk] = useState<{ busy: boolean; error: string | null }>({ busy: false, error: null });
  const blockingIds = useMemo(() => new Set(blocked.map((b) => b.approval.id)), [blocked]);
  const sections = useMemo(() => inboxSections(groups, grouping), [groups, grouping]);
  const visible: InboxItem[] = useMemo(() => (scope === 'done' ? done : scope === 'expiring' ? expiring : sections.flatMap((g) => g.items)), [scope, done, expiring, sections]);
  const current = visible.find((i) => i.key === selected) ?? null;

  // Bulk (ADR 0072): pending requests, questions and asked questions can be picked, never anything
  // blocked. Shift ranges and Select all follow the rows on screen.
  const pickable = useMemo(() => visible.filter(isSelectable), [visible]);
  const pickKeys = useMemo(() => pickable.map((i) => i.key), [pickable]);
  const sel = useSelection(pickKeys);
  const picked = useMemo(() => pickable.filter((i) => sel.selected.has(i.key)), [pickable, sel.selected]);
  const [outcome, setOutcome] = useState<BulkOutcome | null>(null);
  const onCheck = useCallback((key: string, range: boolean) => sel.toggle(key, range), [sel]);

  // Deep link: ?approval=<id> selects that item (and opens the drawer on a narrow screen).
  const deep = params.get('approval');
  useEffect(() => {
    if (!deep) return;
    const k = keyForApproval(deep, groups, done);
    if (!k) return;
    if (k.startsWith('d:') && scope !== 'done') return setParams((p) => (p.set('view', 'done'), p), { replace: true });
    setSelected(k);
    setDrawerOpen(true);
    rowRefs.current.get(k)?.scrollIntoView({ block: 'nearest' });
  }, [deep, groups, done, scope, setParams]);

  // Docked: one item open while there is one. The open item stays put as others arrive (never
  // swapped under the pointer); a gone item hands over to its neighbour.
  const lastVisible = useRef<InboxItem[]>(visible);
  useEffect(() => {
    if (selected && !visible.some((i) => i.key === selected)) {
      const next = nextAfter(lastVisible.current, selected);
      setSelected(next && visible.some((i) => i.key === next) ? next : wide ? (visible[0]?.key ?? null) : null);
    } else if (!selected && wide && visible[0]) setSelected(visible[0].key);
    lastVisible.current = visible;
  }, [visible, selected, wide]);
  useEffect(() => {
    shownAt.current = Date.now();
  }, [current?.key]);
  // On a narrow screen the session drawer takes the Inbox drawer's place: never two stacked.
  const sessionOpen = params.get('session');
  useEffect(() => {
    if (sessionOpen && !wide) setDrawerOpen(false);
  }, [sessionOpen, wide]);

  const select = useCallback(
    (key: string) => {
      setSelected(key);
      setDrawerOpen(true);
      if (deep) {
        params.delete('approval');
        setParams(params, { replace: true });
      }
    },
    [deep, params, setParams],
  );
  const setGrouping = (g: InboxGrouping) => {
    const n = new URLSearchParams(params);
    if (g === 'kind') n.delete('group');
    else n.set('group', g);
    setParams(n, { replace: true });
  };
  const setScope = (s: InboxScope) => {
    const n = new URLSearchParams(params);
    if (s === 'open') n.delete('view');
    else n.set('view', s);
    n.delete('approval');
    setParams(n, { replace: true });
    setSelected(null);
  };

  const ctxOf = useCallback(
    (a: Pick<ApprovalRequest, 'machineId' | 'projectId' | 'sessionId' | 'missionId'>): DetailContext => {
      const s = sessions[a.sessionId];
      const missionId = a.missionId ?? missions?.find((m) => m.sessionIds.includes(a.sessionId) || m.launches.some((l) => l.sessionId === a.sessionId))?.id;
      return {
        machine: machines[a.machineId]?.name,
        project: a.projectId ? projects[a.projectId]?.name : undefined,
        projectId: a.projectId,
        session: s ? titleOf(s).title : undefined,
        agentKind: s?.agentKind,
        mission: missionId ? missions?.find((m) => m.id === missionId)?.title : undefined,
      };
    },
    [machines, projects, sessions, missions, titleOf],
  );
  const missionIdOf = useCallback((a: ApprovalRequest) => a.missionId ?? missions?.find((m) => m.sessionIds.includes(a.sessionId) || m.launches.some((l) => l.sessionId === a.sessionId))?.id ?? null, [missions]);

  // Keyboard: j and k move (and open the panel on a narrow screen); a, s, r, d and Esc go to the
  // request open on screen, only while its panel shows, never inside another dialog or menu.
  const panelOpen = !!current && (wide || drawerOpen);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing || e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      if (otherOverlayOpen()) return;
      if (!visible.length) return;
      const i = Math.max(0, visible.findIndex((p) => p.key === current?.key));
      const move = (to: number) => {
        const p = visible[Math.min(visible.length - 1, Math.max(0, to))];
        if (!p) return;
        setSelected(p.key);
        setDrawerOpen(true);
        rowRefs.current.get(p.key)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      };
      const k = e.key;
      if (k === 'j' || k === 'ArrowDown') return (e.preventDefault(), move(current && panelOpen ? i + 1 : i));
      if (k === 'k' || k === 'ArrowUp') return (e.preventDefault(), move(i - 1));
      if (k === 'x' && current && panelOpen && isSelectable(current)) return (e.preventDefault(), sel.toggle(current.key, e.shiftKey));
      if (!panelOpen || current?.kind !== 'request') return;
      if (Date.now() - shownAt.current < KEY_SETTLE_MS) return;
      if (answerKeys.current?.(e)) e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, current, panelOpen, sel]);

  const registerRef = useCallback((key: string, el: HTMLElement | null) => {
    if (el) rowRefs.current.set(key, el);
    else rowRefs.current.delete(key);
  }, []);

  if (phase === 'loading') return <InboxSkeleton />;
  if (phase === 'missing') {
    return <EmptyState size="page" icon={<PlugZap />} title="Approvals are not available on this hub" description="Restart the hub on the current Orgus version to answer requests here." />;
  }

  // -- Row views ------------------------------------------------------------
  const rowView = (item: InboxItem): RowView => {
    // Where an item comes from, as the row's two quiet facts: the session after its agent's mark, the project after its own.
    const where = (c: DetailContext, fallback?: string) => [
      c.mission ?? c.session ? <SessionFact key="s" kind={c.agentKind} name={(c.mission ?? c.session)!} /> : fallback ? <span key="s" className="truncate">{fallback}</span> : null,
      c.project ? <PlaceFact key="p" projectId={c.projectId} name={c.project} /> : c.machine ? <PlaceFact key="p" projectId={null} name={c.machine} /> : null,
    ];
    const sessionCtx = (s: Session): DetailContext => ({ session: titleOf(s).title, agentKind: s.agentKind, project: s.projectId ? projects[s.projectId]?.name : undefined, projectId: s.projectId, machine: machines[s.machineId]?.name });
    switch (item.kind) {
      case 'request': {
        const a = item.approval;
        const c = ctxOf(a);
        const left = a.expiresAt - now;
        const urgent = left < 3 * 60_000;
        const Glyph = TOOL_GLYPH[toolFamily(a.tool)];
        // The glyph and the title's verb already say the tool, so the meta is where it comes from:
        // the session (left out when the group header says it) and the project.
        const bySession = grouping === 'session' && scope === 'open';
        return {
          lead: <Glyph />,
          title: <RequestTitle a={a} />,
          // On a phone the mission stays: it says what the request belongs to, the project is the detail.
          meta: [bySession ? null : where(c)[0], bySession ? null : where(c)[1]],
          chip: <RiskLabel risk={a.risk} />,
          trailing: (
<span className={cn(urgent && 'font-medium text-status-attention')} title="Time left to answer before it expires">{left > 0 ? `${short(left)} left` : 'Expiring'}</span>
          ),
          label: `request, needs you`,
        };
      }
      case 'ask': {
        const a = item.approval;
        const c = ctxOf(a);
        const sum = askSummary(a);
        const left = a.expiresAt - now;
        const urgent = left < 3 * 60_000;
        const Glyph = ASK_GLYPH[item.ask];
        return {
          lead: <Glyph />,
          title: <span className="truncate">{sum.title}</span>,
          // By session the header says where it comes from and the glyph says what it is: nothing repeats under the title.
          meta: [grouping === 'session' && scope === 'open' ? null : where(c)[0], sum.server ? <PlaceFact key="p" projectId={null} name={sum.server} server /> : grouping === 'session' && scope === 'open' ? null : where(c)[1]],
          narrowLast: true,
          chip: sum.chip ? <Badge>{sum.chip}</Badge> : undefined,
          chipNarrow: false,
          trailing: (
<span className={cn(urgent && 'font-medium text-status-attention')} title="Time left to answer before it expires">{left > 0 ? `${short(left)} left` : 'Expiring'}</span>
          ),
          label: `${ASK_WORD[item.ask]}, needs you`,
        };
      }
      case 'blocked': {
        const a = item.approval;
        const c = ctxOf(a);
        const resuming = item.block.phase === 'resuming';
        const failed = item.block.phase === 'resume_failed' || item.block.phase === 'not_run';
        const Glyph = TOOL_GLYPH[toolFamily(a.tool)];
        const cause = resuming ? `${a.resume?.via === 'message' ? 'Continuing' : 'Resuming'} on ${c.machine ?? 'its machine'}` : failed ? 'Resume failed' : blockedCause(a);
        return {
          lead: resuming ? <WorkStateIcon state="running" /> : failed ? <WorkStateIcon state="failed" /> : <Glyph />,
          title: <span className="truncate">{c.mission ?? c.session ?? a.title}</span>,
          meta: [<span key="c" className="truncate">{cause.charAt(0).toUpperCase() + cause.slice(1)}</span>, where(c)[1]],
          chip: <RiskLabel risk={a.risk} />,
          // One state per row: an expired request is not still waiting, so its age says when it expired.
          trailing: resuming ? 'Resuming' : a.state === 'expired' ? <Span full={`expired ${short(now - (a.decidedAt ?? item.at))} ago`} short={short(now - (a.decidedAt ?? item.at))} /> : <Span full={`stopped ${short(now - item.at)} ago`} short={short(now - item.at)} />,
          label: `blocked`,
        };
      }
      case 'step': {
        const p = item.step;
        return {
          lead: <MessagesSquare />,
          title: <span className="truncate">{p.step.title}</span>,
          meta: [<span key="c" className="truncate">{p.conversationTitle}</span>],
          chip: p.step.tier === 'critical' ? <RiskLabel risk="high" /> : undefined,
          trailing: <Span full={`waiting ${short(now - item.at)}`} short={short(now - item.at)} />,
          label: `assistant step, needs you`,
        };
      }
      case 'question':
      case 'asked': {
        const s = item.session;
        const q = plainText(sessionReason(s));
        const asked = item.kind === 'asked';
        return {
          lead: asked ? <MessageCircleQuestion /> : <WorkStateIcon state="needs-you" />,
          title: <span className="truncate">{q || (asked ? 'Asks you a question' : 'Waits for you in its session')}</span>,
          meta: where(sessionCtx(s)),
          narrowLast: true,
          trailing: asked && item.kind === 'asked' && item.open ? <Span full="Open question" short="Open" /> : <Span full={asked ? `asked ${short(now - item.at)} ago` : `waiting ${short(now - item.at)}`} short={short(now - item.at)} />,
          label: asked ? (item.kind === 'asked' && item.open ? 'open question' : 'asked') : 'session waiting, needs you',
        };
      }
      case 'dismissed': {
        const x = item.record;
        return {
          lead: <BellOff />,
          title: <span className="truncate">{x.question || 'A question'}</span>,
          // The time is the row's own column: the meta says who, or until when.
          meta: [<span key="w" className="truncate">{x.kind === 'dismiss' ? `Dismissed by ${who(x.by)}` : setAsideText(x, who)}</span>, item.session ? where(sessionCtx(item.session))[0] : null],
          trailing: formatRelative(item.at, now),
          label: x.kind === 'dismiss' ? 'dismissed' : 'snoozed',
        };
      }
      case 'done': {
        const a = item.approval;
        const c = ctxOf(a);
        // Passed back and answered in the session (ADR 0056): the session's person answered it.
        const sa = a.sessionAnswer;
        const Icon = sa ? (sa.outcome === 'answered' ? Check : X) : a.state === 'allowed' ? Check : a.state === 'denied' ? X : TimerOff;
        const by = a.onBehalfOf ? decidedText(a, (by) => (by ? who(by) : 'someone')) : sa ? (sa.by ? `by ${who(sa.by)}` : null) : a.state === 'expired' ? null : a.delegate ? `by ${a.delegate.agent}, policy v${a.delegate.version}` : a.decidedBy === 'policy' ? 'by policy' : `by ${a.decidedBy ?? 'someone'}`;
        return {
          lead: <Icon />,
          title: isAnswerable(a) ? <span className="truncate">{askSummary(a).title}</span> : <RequestTitle a={a} />,
          meta: [<span key="d" className="truncate">{[doneLabel(a), by].filter(Boolean).join(' ')}</span>, where(c)[1]],
          trailing: formatRelative(item.at, now),
          label: 'decided',
        };
      }
    }
  };

  // -- Bulk: asked questions over a day old (never anything blocked) ---------
  const staleAsked = openAsked(groups);
  const dismissStale = async () => {
    setBulk({ busy: true, error: null });
    try {
      const n = await dismissAsks(staleAsked.map((i) => ({ askId: askIdOf(i.session), sessionId: i.session.id, reason: '' })));
      toast(`${n} ${n === 1 ? 'question' : 'questions'} dismissed`);
      setBulk({ busy: false, error: null });
    } catch (e) {
      const err = toUiError(e);
      setBulk({ busy: false, error: `${err.what} ${err.why ?? ''} ${err.next ?? ''}`.replace(/\s+/g, ' ').trim() });
    }
  };

  // -- The strip ------------------------------------------------------------
  // The oldest wait among what counts: an open question (over a day) does not (ADR 0056).
  const oldest = waiting.needs.oldest;
  const today = doneToday(approvals?.recent ?? [], now) + setAsideList.filter((x) => x.at >= new Date(new Date(now).toDateString()).getTime()).length;
  const doneFact = !done.length ? 'none yet' : today < done.length ? `${formatInt(today)} today` : `last ${short(Math.max(0, now - done[0]!.at))} ago`;
  const segments: Segment[] = [
    { id: 'open', label: 'Needs you', icon: <Hand />, tone: waiting.total ? 'attention' : 'neutral', value: waiting.total, fact: oldest != null ? `oldest ${short(now - oldest)}` : 'nothing waiting', loud: waiting.urgent > 0 },
    { id: 'expiring', label: 'Expiring soon', short: 'Expiring', icon: <Timer />, tone: expiring.length ? 'attention' : 'neutral', value: expiring.length, fact: 'under 3m' },
    // Done counts what its list shows; its fact is how many are from today, or, when that is all of
    // them (the number again), how long ago the last one was.
    { id: 'done', label: 'Done', icon: <CircleCheckBig />, tone: 'neutral', value: done.length, fact: doneFact },
  ];

  // -- The list -------------------------------------------------------------
  const rows = (items: readonly InboxItem[]) =>
    items.map((i) => (
      <Row
        key={i.key}
        item={i}
        view={rowView(i)}
        selected={current?.key === i.key && (wide || drawerOpen)}
        onSelect={select}
        registerRef={registerRef}
        check={scope !== 'done' && isSelectable(i) ? { checked: sel.isSelected(i.key), any: sel.count > 0, onCheck } : undefined}
      />
    ));
  /** A section's select-all box, when it holds rows that can be picked. */
  const groupCheck = (items: InboxItem[]): GroupCheck | undefined => {
    const keys = items.filter(isSelectable).map((i) => i.key);
    if (!keys.length) return undefined;
    const on = keys.filter((k) => sel.isSelected(k)).length;
    return {
      checked: on === keys.length,
      partial: on > 0 && on < keys.length,
      any: sel.count > 0,
      onToggle: () => {
        const all = on === keys.length;
        for (const k of keys) if (sel.isSelected(k) === all) sel.toggle(k);
      },
    };
  };
  /** A section's rows, folded past SECTION_ROWS, with the fold that opens it in place. */
  const sectionRows = (key: string, items: readonly InboxItem[]) => {
    const all = whole.has(key);
    const shown = shownRows(items, selected, all);
    return (
      <>
        <ul className={cn('flex flex-col gap-1', GROUP_INDENT, 'max-sm:pl-0')}>{rows(shown)}</ul>
        {items.length > SECTION_ROWS && (all || shown.length < items.length) && (
          <div className={cn('flex', GROUP_INDENT, 'max-sm:pl-0')}>
          <Pressable
            onClick={() =>
              setWhole((w) => {
                const n = new Set(w);
                if (!n.delete(key)) n.add(key);
                return n;
              })
            }
            aria-expanded={all}
            className="mx-3 my-1 flex w-auto items-center gap-1 rounded-control text-caption text-fg-subtle transition-colors duration-(--duration-fast) hover:text-fg-muted"
          >
            {all ? 'Show less' : `Show ${formatInt(items.length - shown.length)} more`}
            <ChevronDown className={cn('size-3 transition-transform duration-(--duration-fast)', all && 'rotate-180')} aria-hidden />
          </Pressable>
          </div>
        )}
      </>
    );
  };
  const sessionName = (id: string) => {
    const s = sessions[id];
    return s ? titleOf(s).title : 'a session';
  };

  const empty =
    scope === 'done' ? (
      <EmptyState icon={<InboxIcon />} title="No answers yet" description="Your answers, policy refusals and timeouts show here." />
    ) : scope === 'expiring' ? (
      <EmptyState icon={<Timer />} title="Nothing expires soon" description="Requests and questions with under 3 minutes left show here." />
    ) : (
      <EmptyState icon={<CircleCheckBig />} title="Nothing needs you" description="Requests and questions from your sessions wait here for an answer." />
    );

  const personSection =
    scope === 'open' && leftForPerson.length > 0 ? (
      <section key="left-for-a-person" aria-label="Left for a person" className="flex flex-col">
        <GroupHeader label="Left for a person" count={leftForPerson.length} />
        <ul className={cn('flex flex-col gap-1', GROUP_INDENT, 'max-sm:pl-0')}>
          {leftForPerson.map((p) => (
            <li key={`${p.missionId}:${p.step}`} className="motion-arrive" data-row>
              <Pressable onClick={() => go(`/missions/${encodeURIComponent(p.missionId)}`)} className="flex h-row-2line items-center gap-3 rounded-card px-3 hover:bg-hover">
                <Hand className="size-4 shrink-0 text-status-attention" aria-hidden />
                <span className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="truncate text-ui font-medium text-fg">{p.step}</span>
                  <span className="flex min-w-0 items-center gap-3 text-caption text-fg-subtle">
                    <span className="truncate">{p.title}</span>
                    {projects[p.projectId]?.name && <span className="shrink-0">{projects[p.projectId]!.name}</span>}
                  </span>
                </span>
                <span className="shrink-0 whitespace-nowrap text-caption text-fg-subtle num">{formatRelative(p.at, now)}</span>
              </Pressable>
            </li>
          ))}
        </ul>
      </section>
    ) : null;

  const list = (
    <div className="flex flex-col gap-4">
      {visible.length === 0 ? (
        (personSection ?? <div className={cn(SURFACE, 'motion-arrive')}>{empty}</div>)
      ) : scope === 'open' ? (
        [...sections.map((g) => {
          const oldestInGroup = g.items.reduce((m, i) => Math.min(m, i.at), Infinity);
          const s = g.sessionId ? sessions[g.sessionId] : undefined;
          const label = g.sessionId ? sessionName(g.sessionId) : g.tool ?? GROUP_LABEL[g.kind];
          const Glyph = g.tool ? TOOL_GLYPH[toolFamily(g.tool.split(' ')[0])] : null;
          // By session: the header says the session's project once (its rows leave it out); each
          // row's glyph and verb say its tool, so the header does not list them again.
          const place = s?.projectId ? projects[s.projectId]?.name : s ? machines[s.machineId]?.name : undefined;
          return (
            <section key={g.key} aria-label={label} className="flex flex-col">
              <GroupHeader
                label={g.tool ? <span className="font-mono">{g.tool}</span> : <span className={g.sessionId ? 'line-clamp-2' : 'truncate'}>{label}</span>}
                count={g.items.length}
                lead={g.sessionId ? <AgentGlyph kind={s?.agentKind} className="size-4" /> : Glyph ? <Glyph className="size-4 text-fg-subtle" aria-hidden /> : undefined}
                check={groupCheck(g.items)}
                meta={g.sessionId ? (place ?? null) : g.kind === 'request' || g.kind === 'ask' || g.kind === 'asked' ? null : `oldest ${short(now - oldestInGroup)}`}
                state={g.kind === 'asked' ? 'asked' : 'needs-you'}
                action={g.kind === 'asked' && staleAsked.length > 0 ? (
                  <Tooltip content={`Dismiss ${staleAsked.length === 1 ? 'the asked question' : `the ${formatInt(staleAsked.length)} asked questions`} older than a day`}>
                    <Button size="sm" variant="ghost" icon={<BellOff />} loading={bulk.busy} onClick={() => void dismissStale()}>
                      Dismiss old questions
                    </Button>
                  </Tooltip>
                ) : undefined}
              />
              {g.kind === 'asked' && bulk.error && <p role="alert" className="mx-3 mb-2 rounded-control bg-status-danger-soft px-3 py-2 text-ui text-fg">{bulk.error}</p>}
              {sectionRows(g.key, g.items)}
            </section>
          );
        }), personSection]
      ) : (
        <section className="flex flex-col">
          <GroupHeader label={scope === 'done' ? 'Done' : 'Expiring soon'} count={visible.length} state={scope === 'done' ? 'landed' : 'needs-you'} />
          {sectionRows(scope, visible)}
        </section>
      )}
    </div>
  );

  // -- The panel ------------------------------------------------------------
  const detail = (render: (p: DetailParts) => ReactNode) =>
    current && (
      <InboxDetail
        item={current}
        ctx={current.kind === 'question' || current.kind === 'asked' ? { project: current.session.projectId ? projects[current.session.projectId]?.name : undefined, machine: machines[current.session.machineId]?.name } : current.kind === 'step' || current.kind === 'dismissed' ? {} : ctxOf(current.approval)}
        title={current.kind === 'question' || current.kind === 'asked' ? titleOf(current.session).title : current.kind === 'dismissed' && current.session ? titleOf(current.session).title : ''}
        now={now}
        request={{ bindKeys, keys: true }}
        stillBlocks={current.kind === 'done' && blockingIds.has(current.approval.id)}
        missionId={current.kind === 'done' ? missionIdOf(current.approval) : null}
        onStepDone={() => undefined}
        frame={render}
      />
    );

  const docked = (p: DetailParts) => (
    <aside key={current?.key} aria-label={p.title} className={cn(SURFACE, 'flex min-h-0 flex-1 flex-col rounded-overlay')}>
      <header className="flex shrink-0 flex-col gap-3 px-6 pb-4 pt-6">
        <div className="flex min-h-control-sm items-center gap-2">
          {p.status}
          <span className="ml-auto flex items-center gap-1">{p.tools}</span>
        </div>
        <h2 className="motion-arrive min-w-0 text-lead font-medium text-fg">{p.heading ?? <span className="line-clamp-2">{p.title}</span>}</h2>
        {p.context && <p className="truncate text-caption text-fg-subtle">{p.context}</p>}
        {p.facts && <div className="motion-arrive pt-1">{p.facts}</div>}
      </header>
      <div className="motion-arrive min-h-0 flex-1 overflow-y-auto border-t border-border-subtle px-6 pb-6 pt-6">{p.body}</div>
      {/* While rows are picked the bar below answers them: the panel's own answers step aside, so two sets never compete. */}
      {p.footer && (
        <div className="flex min-h-control-md shrink-0 items-center border-t border-border-subtle px-6 py-3">
          {sel.count > 0 ? <span className="text-caption text-fg-subtle">{formatInt(sel.count)} selected, answered from the bar</span> : p.footer}
        </div>
      )}
    </aside>
  );

  const drawer = (p: DetailParts) => (
    <Drawer
      open={drawerOpen && !!current}
      onOpenChange={setDrawerOpen}
      width={640}
      className={INBOX_DRAWER}
      // Rows: the title gets the full width and two lines on a phone, not one cut line beside the tools.
      layout="rows"
      title={p.heading ?? p.title}
      eyebrow={p.status}
      description={
        p.context || p.facts ? (
          <div className="flex flex-col gap-3">
            {p.context && <span className="text-caption text-fg-subtle">{p.context}</span>}
            {p.facts}
          </div>
        ) : undefined
      }
      actions={p.tools}
      footer={p.footer}
      // The line under the facts, as in the docked panel.
      bodyClassName="border-t border-border-subtle px-6 py-6"
    >
      {p.body}
    </Drawer>
  );

  return (
    <div className="flex h-full min-h-0">
      {/* The list scrolls in its own box, so that box hosts the bulk bar: it pads its foot by the bar's height (BULK_BAR_SPACE) and the bar spans the list, not the docked detail. */}
      <div data-bulk-host="" className="min-h-0 min-w-0 flex-1 overflow-y-auto">
        <div className={cn('mx-auto flex w-full flex-col gap-6 px-6 pb-12 pt-6 max-md:px-4', !wide && 'max-w-(--layout-width-detail)')}>
          <ScopeStrip segments={segments} scope={scope} onScope={setScope} />
          {scope === 'open' && visible.length > 0 && (
            <div className="-mb-2 flex items-center justify-end gap-2">
              <span className="text-caption text-fg-subtle" aria-hidden>
                Group by
              </span>
              <Segmented<InboxGrouping>
                label="Group by"
                size="sm"
                value={grouping}
                onChange={setGrouping}
                options={[
                  { value: 'kind', label: 'Kind' },
                  { value: 'session', label: 'Session' },
                  { value: 'tool', label: 'Tool' },
                ]}
              />
            </div>
          )}
          {outcome && <OutcomeBanner outcome={outcome} onClose={() => setOutcome(null)} />}
          {list}
          {/* While rows are picked, the bar sits on a soft fade of the page: the rows scrolling under it
              dim out rather than peek above it. */}
          <div className={cn('pointer-events-none sticky bottom-0 z-(--z-sticky) -mx-6 -mb-12 -mt-6 flex flex-col items-center px-6 max-md:-mx-4 max-md:px-4', picked.length > 0 && 'bg-linear-to-t from-bg from-60% to-transparent pb-6 pt-12')}>
            <InboxBulk selected={picked} total={pickable.length} onClear={sel.clear} onOutcome={setOutcome} sessionName={sessionName} />
          </div>
        </div>
      </div>
      {wide && current && <div className="flex w-(--drawer-width) shrink-0 flex-col py-2 pr-2 2xl:w-(--drawer-width-wide)">{detail(docked)}</div>}
      {!wide && current && detail(drawer)}
    </div>
  );
}

